// src/pages/Home.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import Features from "../components/Features";
import Testimonials from "../components/Testimonials";
import AlumniWorkedAt from "../components/AlumniWorkedAt";
import CourseCards from "../components/CourseCards";

import "../styles/hero.css";
import "../styles/home.css";

export default function Home() {
  const benefitPills = [
    "E-Learning",
    "Safe Health",
    "Save Travel Time",
    "Confidence",
    "Career Counselling",
  ];

  const whyCards = [
    {
      title: "Experienced Trainers",
      text: "Learn from CA, CMA, and industry experts with real corporate experience.",
    },
    {
      title: "AI & Modern Accounting Tools",
      text: "Hands-on exposure to AI tools, automation, advanced Excel, and modern accounting software.",
    },
    {
      title: "100% Job Placement Support",
      text: "Dedicated placement team, mock interviews, resume building, and job referrals.",
    },
    {
      title: "Live Lectures",
      text: "Live & interactive lectures conducted by experienced industry professionals.",
    },
    {
      title: "Structured Curriculum",
      text: "Industry-aligned syllabus with clear milestones and practical outcomes.",
    },
    {
      title: "Online Classes",
      text: "Attend live and recorded classes anytime, anywhere using our learning platform.",
    },
    {
      title: "Offline Classes",
      text: "Classroom-based training at nearby centres with personal mentoring.",
    },
  ];

  /* COUNT UP */
  const statRef = useRef(null);
  const [activeStudents, setActiveStudents] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) setStarted(true);
      },
      { threshold: 0.4 }
    );
    if (statRef.current) observer.observe(statRef.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;

    let current = 0;
    const end = 1500;
    const step = end / (1600 / 16);

    const timer = setInterval(() => {
      current += step;
      if (current >= end) {
        setActiveStudents(end);
        clearInterval(timer);
      } else {
        setActiveStudents(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [started]);

  /* IMAGE PARALLAX EFFECT */
  useEffect(() => {
    const stack = document.querySelector(".hero2-image-stack");
    if (!stack) return;

    const move = (e) => {
      const x = (e.offsetX / stack.clientWidth - 0.5) * 10;
      const y = (e.offsetY / stack.clientHeight - 0.5) * 10;

      stack.querySelectorAll("img").forEach((img, i) => {
        const depth = (i + 1) * 4;
        img.style.transform = `translate(${x * depth}px, ${y * depth}px)`;
      });
    };

    const reset = () => {
      stack.querySelectorAll("img").forEach((img) => {
        img.style.transform = "translate(0,0)";
      });
    };

    stack.addEventListener("mousemove", move);
    stack.addEventListener("mouseleave", reset);

    return () => {
      stack.removeEventListener("mousemove", move);
      stack.removeEventListener("mouseleave", reset);
    };
  }, []);

  return (
    <main>

      {/* ============= HERO SECTION ============= */}
      <section className="hero-image2">
        <div className="hero2-container">
          <div className="hero2-left">
            <span className="hero2-badge">#1 Job Oriented Accounting Program</span>
            <h1>
              Transforming Commerce Graduates <br /> into Professionals
            </h1>
            <p>
              Certificate Program for Accounts Executive (CPAE) — practical,
              placement-focused, industry-ready with live sessions and certification.
            </p>
            <Link to="/register" className="hero2-btn">Enroll Our Course Now</Link>
          </div>

          <div className="hero2-right">
            <img src="/images/student.png" alt="Student" className="hero2-image" />
            <div className="hero2-stat hero2-years">
              <strong>12+</strong><span>Years in the Industry</span>
            </div>
            <div className="hero2-stat hero2-rating">
              ⭐ <strong>4.3</strong><span>5k+ Feedbacks</span>
            </div>
          </div>
        </div>
      </section>

      {/* HERO BOTTOM */}
      <section className="hero-bottom-info">
        <div className="hero-bottom-card">
          <div className="info-left">
            <h3>Join Our Community <br /> And Start Your Journey.</h3>
            <Link to="/register" className="info-link">LET’S GO</Link>
          </div>

          <div className="info-center">
            <div className="info-number">400+</div>
            <p>
              Connect with world-class mentors for expert guidance,
              real insights, and personalized support to achieve your goals.
            </p>
          </div>

          <div className="info-right">
            <div className="rating-box">
              <span className="rating-score">4.3⭐</span>
              <p>In all the platforms Combined</p>
              <div className="avatars">
                <img src="/images/u1.jpg" alt="student" />
                <img src="/images/u2.jpg" alt="student" />
                <img src="/images/u3.jpg" alt="student" />
                <span className="more">100+</span>
              </div>
              <strong>5k+</strong><span>Total Feedbacks</span>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section about-home">
        <div className="container about-layout">
          <div className="about-left">
            <div className="about-media">
              <img src="/images/classrom.png" alt="Students learning" />
            </div>
            <div className="about-stat-card">
              <div className="about-stat-number">1500+</div>
              <div className="about-stat-text">Students trained <br /> in practical accounting</div>
            </div>
          </div>

          <div className="about-right">
            <p className="about-kicker">ABOUT OUR INSTITUTE</p>
            <h2 className="about-title">Welcome to Concerted Path</h2>
            <p className="about-body">
              Concerted Path is a specialised training institute focused on making
              commerce graduates job-ready for accounting and finance roles.
            </p>
            <p className="about-body">
              We combine classroom learning with real-world tools, accounting
              software, and case studies used by professionals in the industry.
            </p>
            <p className="about-body">
              The Computerized Accounting Program covers Tally Prime, BUSY Accounting,
              Taxation, SAP, Power BI, Advanced Excel, Computer Basics, and AI awareness.
              The course helps students develop practical digital skills for finance and
              business, making it an ideal choice for those seeking a rewarding and
              future-ready career.
            </p>
            <ul className="about-bullets">
              <li>Structured 6-month CPAE curriculum with clear milestones</li>
              <li>Hands-on practice with invoices, ledgers & GST returns</li>
              <li>Dedicated mock interviews, CV & placement preparation</li>
              <li>Strong placement partnerships for fresher roles</li>
            </ul>
            <Link to="/about" className="btn btn-dark">Know More About Us</Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section why-section">
        <div className="container why-layout">
          <div className="why-left">
            <div className="why-media">
              <img src="/images/students.png" alt="Students in class" />
            </div>
            <div className="why-stat" ref={statRef}>
              <div className="why-stat-number">
                {(activeStudents / 1000).toFixed(1)}k+
              </div>
              <div className="why-stat-sub">Active Students</div>
            </div>
            <div className="why-cta">
              <button className="btn btn-primary">Download App</button>
            </div>
          </div>

          <div className="why-right">
            <p className="section-kicker">WHY CHOOSE US</p>
            <h2 className="why-title">Why Choose Concerted Path?</h2>
            <p className="why-intro">
              We focus on practical learning, professional confidence, and
              assured placement support to transform fresh graduates into
              industry-ready accounting professionals.
            </p>
            <div className="why-cards">
              {whyCards.map((c, i) => (
                <article key={i} className="why-feature-card">
                  <h4>{c.title}</h4>
                  <p>{c.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>

        <div className="container">
          <div className="why-pill-row">
            {benefitPills.map((pill, i) => (
              <span key={i} className="why-pill">{pill}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PLACEMENT SECTION */}
      <section className="placement-section">
        <div className="placement-layout placement-grid">
          <div className="placement-left">
            <span className="placement-badge">Placement Assurance</span>
            <h2 className="placement-title">
              100% Job <span>Placement Guarantee</span>
            </h2>
            <p className="placement-text">
              Concerted Path offers a <strong>job-oriented training program</strong> designed to
              prepare <strong>B.Com final-year students, Commerce graduates and Class 12th passout students.</strong>.
            </p>
            <p className="placement-text">
              Dedicated <strong>Training & Placement team</strong> bridging academia
              and corporate expectations.
            </p>
            <p className="placement-text">
              In collaboration with <strong>Top Company's</strong>, placement is ensured.
            </p>
            <ul className="placement-points">
              <li>Aptitude & analytical skills</li>
              <li>Technical & domain-specific knowledge</li>
              <li>Professional workplace skills</li>
              <li>Communication & interpersonal skills</li>
            </ul>
            <Link to="/register" className="btn btn-gold">Start Your Career Journey</Link>
          </div>

          <div className="placement-right">
            <img src="/images/job-placements.png" alt="Placement Guarantee" />
          </div>
        </div>
      </section>

      {/* COURSES */}
      <section className="section" style={{ background: "#f7f9fb" }}>
        <div className="container">
          <div className="heading-strip">
            <div className="heading-box">
              <h1>Our Job Oriented Courses</h1>
            </div>
          </div>
          <CourseCards limit={6} />
          <div style={{ textAlign: "center", marginTop: "32px" }}>
            <Link to="/courses" className="btn btn-dark">View All Courses</Link>
          </div>
        </div>
      </section>

      {/* CAREER */}
      <section className="section career-section">
        <div className="container career-layout">
          <div className="career-right">
            <div className="career-image-wrap">
              <img src="/images/career-growth.png" alt="Career Growth" />
            </div>
          </div>

          <div className="career-left">
            <p className="career-kicker">Career Opportunities</p>
            <h2 className="career-title">
              Your Growth Path in <span>Accounting & Finance</span>
            </h2>
            <p className="career-text">Opportunities from small local practices to global firms.</p>
            <ul className="career-list">
              <li>Senior Finance Executive</li>
              <li>Senior / Assistant Accounts Executive</li>
              <li>Accounts Executive (KPOs & MNCs)</li>
              <li>Finance Executive</li>
              <li>MIS Executive</li>
              <li>Inventory Manager / Executive</li>
              <li>AM / Manager Accounts</li>
              <li>Cashier</li>
              <li>Finance / Business Planner</li>
              <li>Billing Executive</li>
              <li>Purchase Manager</li>
              <li>Banking Coordination Executive</li>
            </ul>
            <Link to="/register" className="btn btn-gold">Start Your Career Journey</Link>
          </div>
        </div>
      </section>

      <Features />
      <AlumniWorkedAt />

      <section className="section section-alt">
        <Testimonials />
      </section>

    </main>
  );
}  
